import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { 
  AlertTriangle, 
  User, 
  Phone, 
  Mail, 
  RefreshCw, 
  CheckCircle,
  XCircle
} from 'lucide-react';
import { assignedBookingAPI, greeterAPI } from '../../services/api';
import { useAdminTheme } from '../../contexts/AdminThemeContext';

const UnavailableBookingsCard = () => {
  const { isDarkMode } = useAdminTheme();
  const [unavailableBookings, setUnavailableBookings] = useState([]);
  const [availableGreeters, setAvailableGreeters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [reassigningBooking, setReassigningBooking] = useState(null);
  const [selectedGreeter, setSelectedGreeter] = useState({});
  const [editingReason, setEditingReason] = useState({});
  const [savingReason, setSavingReason] = useState(null);

  useEffect(() => {
    fetchUnavailableBookings();
    fetchAvailableGreeters();
  }, []);

  const fetchUnavailableBookings = async () => {
    try {
      setIsLoading(true);
      const response = await assignedBookingAPI.getUnavailableBookings();
      if (response.success) {
        setUnavailableBookings(response.data.unavailableBookings);
      }
    } catch (error) {
      console.error('Error fetching unavailable bookings:', error);
      toast.error('Failed to fetch unavailable bookings');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAvailableGreeters = async () => {
    try {
      const response = await greeterAPI.getAvailableGreeters();
      if (response.success) {
        setAvailableGreeters(response.data.greeters);
      }
    } catch (error) {
      console.error('Error fetching available greeters:', error);
    }
  };

  const handleReassignBooking = async (bookingId) => {
    const newGreeterId = selectedGreeter[bookingId];
    if (!newGreeterId) {
      toast.error('Please select a greeter for reassignment');
      return;
    }

    try {
      setReassigningBooking(bookingId);
      const response = await assignedBookingAPI.reassignBooking(bookingId, newGreeterId);
      
      if (response.success) {
        toast.success('Booking reassigned successfully');
        // Remove the reassigned booking from the list
        setUnavailableBookings(prev => 
          prev.filter(booking => booking._id !== bookingId)
        );
        // Clear the selected greeter
        setSelectedGreeter(prev => ({ ...prev, [bookingId]: '' }));
      } else {
        toast.error(response.message || 'Failed to reassign booking');
      }
    } catch (error) {
      console.error('Error reassigning booking:', error);
      toast.error('Failed to reassign booking');
    } finally {
      setReassigningBooking(null);
    }
  };

  const handleUpdateReason = async (bookingId, reason) => {
    try {
      setSavingReason(bookingId);
      const response = await assignedBookingAPI.updateUnavailableReason(bookingId, reason);
      
      if (response.success) {
        toast.success('Reason updated successfully');
        // Update the booking in the list
        setUnavailableBookings(prev =>
          prev.map(booking =>
            booking._id === bookingId
              ? { ...booking, unavailableReason: reason }
              : booking
          )
        );
        // Remove from editing state
        setEditingReason(prev => {
          const newState = { ...prev };
          delete newState[bookingId];
          return newState;
        });
      } else {
        toast.error(response.message || 'Failed to update reason');
      }
    } catch (error) {
      console.error('Error updating reason:', error);
      toast.error('Failed to update reason');
    } finally {
      setSavingReason(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${
        isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white'
      }`}>
        <div className="flex items-center justify-center">
          <RefreshCw className={`h-6 w-6 animate-spin transition-colors duration-300 ${
            isDarkMode ? 'text-blue-400' : 'text-primary-600'
          }`} />
          <span className={`ml-2 transition-colors duration-300 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-600'
          }`}>Loading unavailable bookings...</span>
        </div>
      </div>
    );
  }

  if (unavailableBookings.length === 0) {
    return (
      <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${
        isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white'
      }`}>
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <CheckCircle className={`h-8 w-8 transition-colors duration-300 ${
              isDarkMode ? 'text-green-400' : 'text-green-500'
            }`} />
          </div>
          <div className="ml-3">
            <h3 className={`text-lg font-medium transition-colors duration-300 ${
              isDarkMode ? 'text-gray-100' : 'text-gray-900'
            }`}>No Unavailable Bookings</h3>
            <p className={`text-sm transition-colors duration-300 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              All assigned bookings are currently being handled by their assigned greeters.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg shadow-sm border transition-colors duration-300 ${
      isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white'
    }`}>
      <div className={`px-6 py-4 border-b transition-colors duration-300 ${
        isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'
      }`}>
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <AlertTriangle className={`h-6 w-6 transition-colors duration-300 ${
              isDarkMode ? 'text-red-400' : 'text-red-500'
            }`} />
          </div>
          <div className="ml-3">
            <h3 className={`text-lg font-medium transition-colors duration-300 ${
              isDarkMode ? 'text-gray-100' : 'text-gray-900'
            }`}>
              Unavailable Bookings ({unavailableBookings.length})
            </h3>
            <p className={`text-sm transition-colors duration-300 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              Bookings marked as unavailable by greeters that need reassignment.
            </p>
          </div>
        </div>
      </div>

      <div className={`divide-y transition-colors duration-300 ${
        isDarkMode ? 'divide-skyone-border-dark' : 'divide-gray-200'
      }`}>
        {unavailableBookings.map((booking) => (
          <div key={booking._id} className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                {/* Booking Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <h4 className={`text-sm font-medium mb-2 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>Customer Details</h4>
                    <div className="space-y-1">
                      <div className={`flex items-center text-sm transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        <User className="h-4 w-4 mr-2" />
                        {booking.clientName}
                      </div>
                      <div className={`flex items-center text-sm transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        <Mail className="h-4 w-4 mr-2" />
                        {booking.clientEmail}
                      </div>
                      {booking.phoneNumber && (
                        <div className={`flex items-center text-sm transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                          <Phone className="h-4 w-4 mr-2" />
                          {booking.phoneNumber}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className={`text-sm font-medium mb-2 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>Service Details</h4>
                    <div className={`space-y-1 text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      <div><strong>Service:</strong> {booking.serviceType}</div>
                      {booking.description && (
                        <div><strong>Description:</strong> {booking.description}</div>
                      )}
                      {booking.flightDetails?.flightNumber && (
                        <div><strong>Flight:</strong> {booking.flightDetails.flightNumber}</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Original Greeter Info */}
                <div className={`mb-4 p-3 rounded-lg transition-colors duration-300 ${
                  isDarkMode ? 'bg-red-900/20 border border-red-700' : 'bg-red-50'
                }`}>
                  <h5 className={`text-sm font-medium mb-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-red-300' : 'text-red-800'
                  }`}>Originally Assigned To:</h5>
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-8 w-8">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors duration-300 ${
                        isDarkMode ? 'bg-red-900/40' : 'bg-red-100'
                      }`}>
                        <User className={`h-4 w-4 transition-colors duration-300 ${
                          isDarkMode ? 'text-red-400' : 'text-red-600'
                        }`} />
                      </div>
                    </div>
                    <div className="ml-3">
                      <div className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-red-200' : 'text-red-900'
                      }`}>
                        {booking.assignedGreeter?.firstName} {booking.assignedGreeter?.lastName}
                      </div>
                      <div className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? 'text-red-300' : 'text-red-700'
                      }`}>
                        {booking.assignedGreeter?.email}
                      </div>
                    </div>
                    <div className="ml-auto">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition-colors duration-300 ${
                        isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-800'
                      }`}>
                        <XCircle className="h-3 w-3 mr-1" />
                        Unavailable
                      </span>
                    </div>
                  </div>
                  <div className={`mt-2 text-xs transition-colors duration-300 ${
                    isDarkMode ? 'text-red-400' : 'text-red-600'
                  }`}>
                    Marked unavailable on {formatDate(booking.unavailableAt)}
                  </div>
                </div>

                {/* Unavailable Reason Section */}
                <div className={`border-t pt-4 mb-4 transition-colors duration-300 ${
                  isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'
                }`}>
                  <h5 className={`text-sm font-medium mb-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-100' : 'text-gray-900'
                  }`}>Reason for Unavailability:</h5>
                  {editingReason[booking._id] !== undefined ? (
                    <div className="space-y-2">
                      <textarea
                        value={editingReason[booking._id]}
                        onChange={(e) => setEditingReason(prev => ({
                          ...prev,
                          [booking._id]: e.target.value
                        }))}
                        rows={3}
                        className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-300 ${
                          isDarkMode 
                            ? 'bg-skyone-inset-dark border-skyone-border-dark text-gray-100 placeholder-gray-400' 
                            : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                        }`}
                      />
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleUpdateReason(booking._id, editingReason[booking._id])}
                          disabled={savingReason === booking._id}
                          className="px-3 py-1 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {savingReason === booking._id ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => {
                            setEditingReason(prev => {
                              const newState = { ...prev };
                              delete newState[booking._id];
                              return newState;
                            });
                          }}
                          className="px-3 py-1 bg-gray-500 text-white text-xs font-medium rounded hover:bg-skyone-border-dark"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start">
                      <p className={`flex-1 text-sm transition-colors duration-300 ${
                        booking.unavailableReason ? (isDarkMode ? 'text-gray-200' : 'text-gray-700') : (isDarkMode ? 'text-gray-500 italic' : 'text-gray-400 italic')
                      }`}>
                        {booking.unavailableReason || 'No reason provided'}
                      </p>
                      <button
                        onClick={() => setEditingReason(prev => ({
                          ...prev,
                          [booking._id]: booking.unavailableReason || ''
                        }))}
                        className={`ml-2 text-xs font-medium transition-colors duration-300 ${
                          isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-primary-600 hover:text-primary-800'
                        }`}
                      >
                        Edit
                      </button>
                    </div>
                  )}
                </div>

                {/* Reassignment Section */}
                <div className={`border-t pt-4 transition-colors duration-300 ${
                  isDarkMode ? 'border-skyone-border-dark' : ''
                }`}>
                  <h5 className={`text-sm font-medium mb-3 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-100' : 'text-gray-900'
                  }`}>Reassign to Available Greeter:</h5>
                  <div className="flex items-center space-x-3">
                    <select
                      value={selectedGreeter[booking._id] || ''}
                      onChange={(e) => setSelectedGreeter(prev => ({ 
                        ...prev, 
                        [booking._id]: e.target.value 
                      }))}
                      className={`flex-1 px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-300 ${
                        isDarkMode 
                          ? 'bg-skyone-inset-dark border-skyone-border-dark text-gray-100' 
                          : 'border-gray-300 bg-white text-gray-900'
                      }`}
                    >
                      <option value="">Select a greeter...</option>
                      {availableGreeters
                        .filter(greeter => greeter._id !== booking.assignedGreeter._id)
                        .map((greeter) => (
                          <option key={greeter._id} value={greeter._id}>
                            {greeter.firstName} {greeter.lastName} - {greeter.email}
                          </option>
                        ))}
                    </select>
                    
                    <button
                      onClick={() => handleReassignBooking(booking._id)}
                      disabled={!selectedGreeter[booking._id] || reassigningBooking === booking._id}
                      className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {reassigningBooking === booking._id ? (
                        <div className="flex items-center">
                          <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                          Reassigning...
                        </div>
                      ) : (
                        'Reassign Booking'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UnavailableBookingsCard;
